import { Form, Input, Modal, TreeSelect, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { getSceneTree, type SceneTreeNode } from '@/apis/simulation';

const { Text } = Typography;

const ACCENT = '#5e6ad2';

/**
 * 把场景树（SceneTreeNode[]）转成 antd TreeSelect 的 treeData；
 * 剔除 isHidden=true 的节点及其后代（与 CreateSceneQcJobModal 同口径）。
 */
const convertSceneTreeToTreeData = (nodes: SceneTreeNode[]): any[] =>
  nodes
    .filter((node) => !node.isHidden)
    .map((node) => ({
      title: node.name,
      value: node.id,
      key: node.id,
      children:
        node.children && node.children.length > 0
          ? convertSceneTreeToTreeData(node.children)
          : undefined,
    }));

export interface SaveAsSubmitValues {
  newName?: string;
  targetTreeNodeId: number;
  description?: string;
}

export interface SaveAsModalProps {
  open: boolean;
  /** 基准场景名（展示用，提示用户基于谁另存） */
  baseSceneName?: string;
  /** 基准场景描述（作为新场景描述输入框的默认值，可编辑） */
  baseDescription?: string | null;
  /** 当前已配置的云端控制事件条数（展示用，提示会带多少条到新场景） */
  eventsCount: number;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (values: SaveAsSubmitValues) => void;
}

/**
 * 「另存为新场景」对话框（F1）：只收集「新场景名称」+「目标树节点」。
 *
 * 设计取舍：不内嵌 EventListEditor，events 复用编辑态 editForm 已填的值
 * （single source of truth，不让用户填两遍）。父组件在 onSubmit 里组装 events
 * 并调 saveAsScene。提示卡明示「基于谁复制 + 带几条事件 + 基准不变」。
 */
export function SaveAsModal({
  open,
  baseSceneName,
  baseDescription,
  eventsCount,
  submitting,
  onCancel,
  onSubmit,
}: SaveAsModalProps) {
  const [form] = Form.useForm<SaveAsSubmitValues>();
  const [treeData, setTreeData] = useState<any[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);

  // 打开时拉一次场景树（best-effort；失败留空树，用户可重开重试），
  // 并把基准场景描述预填进描述框（用户可改）。
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ description: baseDescription ?? '' });
    setTreeLoading(true);
    getSceneTree()
      .then((nodes) => setTreeData(convertSceneTreeToTreeData(nodes ?? [])))
      .catch(() => undefined)
      .finally(() => setTreeLoading(false));
  }, [open, baseDescription, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSubmit({
        // 名称不开放自定义：恒传 undefined，由后端自动派生「基准名_序号」。
        newName: undefined,
        targetTreeNodeId: values.targetTreeNodeId,
        description: values.description ?? '',
      });
    } catch {
      /* 校验未过：antd 已在字段下提示，不重复弹 message */
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="另存为新场景"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={submitting}
      okText="确定另存"
      cancelText="取消"
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout="vertical" preserve={false} requiredMark>
        <Form.Item
          label="目标树节点"
          name="targetTreeNodeId"
          extra="须为叶子节点；非叶子会被后端拒绝"
          rules={[{ required: true, message: '请选择目标树节点' }]}
        >
          <TreeSelect
            treeData={treeData}
            loading={treeLoading}
            placeholder="选择新场景归属的叶子节点"
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp="title"
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="场景描述"
          name="description"
          extra="默认沿用基准场景描述，可在此编辑；留空则清空"
        >
          <Input.TextArea
            placeholder="填写新场景的描述备注"
            autoSize={{ minRows: 2, maxRows: 6 }}
            maxLength={2000}
            showCount
          />
        </Form.Item>
      </Form>
      <div
        style={{
          marginTop: 8,
          padding: '8px 12px',
          background: '#f5f6ff',
          border: `1px solid ${ACCENT}22`,
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.6,
        }}
      >
        <Text type="secondary">
          将基于当前场景「{baseSceneName || '-'}」复制一份新场景，叠加当前已配置的
          <b style={{ color: ACCENT }}> {eventsCount} </b>条云端控制事件。
          <br />
          新场景名称自动生成为「基准名_序号」（如 LPL0000132304_1），无需填写。
          <br />
          基准场景不被修改；新场景挂到所选叶子节点下。
        </Text>
      </div>
    </Modal>
  );
}

export default SaveAsModal;
